import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import usePageTracking from "../../../hooks/usePageTracking";
import { activityStart, activityEnd } from "../../../redux/slices/trackActivitySlice";
import "./orderDetails.scss";
import { FiArrowLeft } from "react-icons/fi";
import { fetchOrderById } from "../../../redux/slices/shopSlice";
import { Card } from "react-bootstrap";

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Track Activity
  usePageTracking("ORDER_DETAIL");
  useEffect(() => {
    dispatch(activityStart({ page_name: "ORDER_DETAIL", platform: "web" }));
    return () => dispatch(activityEnd({ page_name: "ORDER_DETAIL" }));
  }, [dispatch]);

  const { selectedOrder: order, loading, error } = useSelector(
    (state) => state.shop,
  );

  // 🔥 Fetch order directly from API
  useEffect(() => {
    dispatch(fetchOrderById(id));
  }, [dispatch, id]);

  if (loading && !order) {
  return (
    <div className="loader-wrapper">
      <div className="spinner"></div>
      <p className="loader-text">Loading your order details...</p>
    </div>
  );
}

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!order) {
    return null;
  }

  const totalItems = order.line_items.reduce(
    (acc, item) => acc + item.quantity,
    0,
  );

  return (
    <div className="order-details-wrapper">
      <div className="app-page-title">
        <div className="page-title-heading d-flex align-items-center">
          <FiArrowLeft
            size={40}
            className="icon cursor-pointer"
            onClick={() => navigate("/shop/my-orders")}
          />
          <h3 className="page-title">Order Confirmed!</h3>
        </div>
      </div>

      <Card className="order-details-container">
        <Card.Body>
          <div className="order-top">
            <div className="title-with-date">
              <h6>Order #{order.number}</h6>
              <p className="order-date">
                {new Date(order.date_created).toLocaleDateString([], {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>

            <span className={`status-badge ${order.status.toLowerCase()}`}>
              {order.status}
            </span>
          </div>

          {/* PRODUCTS */}
          <div className="section">
            <h6>Products</h6>

            {order.line_items.map((item) => (
              <div key={item.id} className="product-row">
                <img src={item.image?.src} alt={item.name} />
                <div className="product-qty">
                  <h6>{item.name}</h6>
                  <p>Quantity: {item.quantity}</p>
                  <p className="price">
                    {order.currency_symbol}
                    {item.total}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* BILLING & SHIPPING */}
          <div className="address-grid">
            <div className="address-box">
              <h6>Billing Details</h6>
              <p>
                {order.billing.first_name} {order.billing.last_name}
              </p>
              <p>{order.billing.address_1}</p>
              <p>
                {order.billing.city}, {order.billing.state}
              </p>
              <p>{order.billing.postcode}</p>
              <p>{order.billing.country}</p>
              <p>Email: {order.billing.email}</p>
              <p>Phone: {order.billing.phone}</p>
            </div>

            <div className="address-box">
              <h6>Shipping Details</h6>
              <p>
                {order.shipping.first_name} {order.shipping.last_name}
              </p>
              <p>{order.shipping.address_1}</p>
              <p>
                {order.shipping.city}, {order.shipping.state}
              </p>
              <p>{order.shipping.postcode}</p>
              <p>{order.shipping.country}</p>
              <p>Phone: {order.shipping.phone}</p>
            </div>
          </div>

          {/* SUMMARY */}
          <div className="summary">
            <div>Total Items: {totalItems}</div>
            <div>
              <strong>
                Total: {order.currency_symbol}
                {order.total}
              </strong>
            </div>
          </div>

          {/* BUTTONS */}
          <div className="buttons-group buttons">
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/shop/my-orders")}
            >
              View Orders
            </button>

            <button
              className="btn btn-primary"
              onClick={() => navigate("/shop/products")}
            >
              Continue Shopping
            </button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default OrderDetails;
